import { useModuleData } from '@/rebuild/hooks/data/useModuleData';
import { merge } from 'lodash';
import { useStore } from '@/utils/vue-api';
import { useFinanceMap } from './useFinanceMap';
import { useFinanceChartOption } from './useFinanceChartOption';

export function useFinanceData() {
	const store = useStore();
	const updateFinanceConfig = data => {
		store.commit('updateModulesConfig', {
			moduleId: 'finance_data',
			config: data || {}
		});
	};
	const getFinanceConfig = () => {
		return store.state.modulesConfig['finance_data'] || {};
	};
	const getFinanceTabs = () => {
		const config = getFinanceConfig();
		return config.tab || [];
	};

	const { main_index, balance_sheet, profit_statement, cash_flow_statement } = useModuleData();

	const moduleIndexMap = new Map([
		['main_index', main_index],
		['balance_sheet', balance_sheet],
		['profit_statement', profit_statement],
		['cash_flow_statement', cash_flow_statement]
	]);
	const loadData = async (type, reportType) => {
		const module = moduleIndexMap.get(type);
		if (module) {
			const paramMap = {
				main_index: { reportTypeYOY: reportType },
				balance_sheet: { reportType },
				profit_statement: { reportTypeYOY: reportType },
				cash_flow_statement: { reportType }
			};
			const params = paramMap[type];
			await module.refresh(params);
			return module.data;
		}
		return {};
	};

	const { getModuleMap } = useFinanceMap();
	const { getChartOption } = useFinanceChartOption();

	const getChartDataFunc = type => {
		const chartConfigs = {
			main_index: (id, data) => {
				const tabs = getFinanceTabs();
				const main_index = tabs?.find(item => item.id === 'main_index');
				const activeTabItem = main_index?.subTabList?.find(tab => tab.id === id);

				const moduleMap = getModuleMap('main_index');
				const configItem = moduleMap[id] || {};
				configItem.name = activeTabItem?.name;
				return getChartOption('main_index', id, data, configItem);
			},
			balance_sheet: (id, data) => {
				const moduleMap = getModuleMap('balance_sheet');
				return getChartOption('balance_sheet', id, data, moduleMap);
			},
			profit_statement: (id, data) => {
				const tabs = getFinanceTabs();
				const profit_statement = tabs?.find(item => item.id === 'profit_statement');
				const activeTabItem = profit_statement?.subTabList?.find(tab => tab.id === id);

				const moduleMap = getModuleMap('profit_statement');
				const configItem = moduleMap[id] || {};
				configItem.name = activeTabItem?.name;
				return getChartOption('profit_statement', id, data, configItem);
			},
			cash_flow_statement: (id, data) => {
				const moduleMap = getModuleMap('cash_flow_statement');
				return getChartOption('cash_flow_statement', id, data, moduleMap);
			}
		};
		return chartConfigs[type];
	};

	function processChartData(func, moduleId, subModuleId, list = []) {
		const fn = func?.(moduleId);
		const chartData = fn(subModuleId, list);

		let legend = [];
		if (chartData.chartOptions?.legend?.data) {
			legend = chartData.chartOptions.legend.data.map(data => data);
		} else {
			legend = chartData.seriesData.map(series => series.name);
		}

		return merge(chartData, {
			chartOptions: {
				grid: { top: 16 },
				legend: { show: false }
			},
			legends: legend
		});
	}

	return {
		updateFinanceConfig,
		getFinanceConfig,
		getFinanceTabs,

		loadData,
		getChartDataFunc,
		processChartData
	};
}
